/**
 * Facility Repository
 * 
 * Handles all facility-related edge function calls.
 * Follows the Repository pattern for clean separation of concerns.
 * 
 * This repository encapsulates all facility data access logic,
 * making it easy to:
 * - Add new facility-related endpoints
 * - Mock data for testing
 * - Handle errors consistently
 * - Cache responses if needed
 */

import EdgeFunctionRepository from './EdgeFunctionRepository';

/**
 * Facility-specific repository
 */
class FacilityRepository {
  /**
   * Get daily climate data for a specific facility
   * 
   * @param {string} facilityId - The facility ID
   * @param {string} date - Optional date (defaults to latest)
   * @returns {Promise<Object>} Facility data with climate information
   * 
   * Response format:
   * {
   *   id: string,
   *   name: string,
   *   address: string,
   *   lat: number,
   *   lng: number,
   *   min_workers: number,
   *   max_workers: number,
   *   brands: string[],
   *   climate: {
   *     os_id: string,
   *     tmin: number,
   *     tmax: number,
   *     humidity_avg: number
   *   },
   *   date: string
   * }
   */
  async getFacilityDailyClimate(facilityId, date = null) {
    try {
      const params = {
        facility_id: facilityId,
        ...(date && { date }),
      };

      const data = await EdgeFunctionRepository.get(
        'facilities-daily-climate',
        params,
        {
          retries: 1, // Retry once on failure
        }
      );

      return data;
    } catch (error) {
      console.error(`Failed to fetch facility climate data for ${facilityId}:`, error);
      throw error;
    }
  }

  /**
   * Example: Get facility historical data
   * (Placeholder for future implementation)
   * 
   * @param {string} facilityId - The facility ID
   * @param {string} startDate - Start date
   * @param {string} endDate - End date
   * @returns {Promise<Object>} Historical climate data
   */
  async getFacilityHistoricalData(facilityId, startDate, endDate) {
    // TODO: Implement when edge function is ready
    throw new Error('Not implemented yet');
  }

  /**
   * Example: Get facilities by region
   * (Placeholder for future implementation)
   * 
   * @param {Object} bounds - Map bounds { north, south, east, west }
   * @returns {Promise<Array>} List of facilities in the region
   */
  async getFacilitiesByRegion(bounds) {
    // TODO: Implement when edge function is ready
    throw new Error('Not implemented yet');
  }

  /**
   * Search facilities
   * 
   * @param {string} query - Search query
   * @param {string} type - Search type ('brand')
   * @returns {Promise<Array>} List of matching facilities
   */
  async searchFacilities(query, type = 'brand', page = 1, limit = 20) {
    try {
      const data = await EdgeFunctionRepository.get(
        'search-facilities',
        { query, type, page, limit }
      );

      return data || [];
    } catch (error) {
      console.error(`Failed to search facilities for ${query} (${type}):`, error);
      return []; // Return empty array on error to prevent UI crash
    }
  }
}

// Export singleton instance
const facilityRepository = new FacilityRepository();
export default facilityRepository;

